/**
 * INDUSTRY SOLUTIONS CONFIG LOADER
 * 
 * Loads static YAML/JSON configuration from local files during application startup.
 * Maintains an in-memory registry for fast lookups.
 * Merges with database-stored client overrides if present.
 * 
 * The actual database only holds:
 * - Industry solution metadata (id, solution_key, name, description, icon, color, is_active)
 * - Client-specific customizations (workflow selections, work item config)
 * - User feature settings (RBAC overrides)
 */

import type {
  OperationalWorkflowTemplateConfig,
  IndustrySolutionDefinition,
  ConfigRegistry,
  ResolvedIndustrySolution,
  QuickActionConfig,
  SmartTaskConfigRoot,
  SmartTaskModalDefinition,
  SmartTaskWorkflowModals,
  SmartTaskShellConstants,
  SmartTaskStatCardsConfig,
  SmartTaskPanelSmartActionsConfig,
  SmartTaskDisplayModesConfig,
  SmartTaskUnifiedFilterTab,
  PermissionDefaultsConfig,
} from './types';

// ============================================================================
// SMARTTASK COMPANION WORKFLOWS
// ============================================================================

/**
 * SmartTask-only "companion" workflows.
 *
 * A companion rides on a *parent* workflow's solution mapping: every solution
 * whose `mappings.workflow_templates` includes the parent also provisions the
 * companion for RBAC purposes — its own permission group, Actions matrix, and
 * Begin Action tile — without the companion itself becoming a standalone
 * portal navigation/dashboard template.
 *
 * `expenses-management` is the companion of `financial-management`: the
 * expenses operational view is folded into the Financial Management tab
 * (`module_context: financial-management`), while expense capture stays an
 * independent workflow for SmartTask tiles and permission-group membership
 * (see `operational-workflows/expenses-management.yaml` and the workflow-key
 * split in BeginActionModal).
 */
export const SMART_TASK_WORKFLOW_COMPANIONS: Record<string, readonly string[]> = {
  'financial-management': ['expenses-management'],
};

/**
 * Expand a solution's `mappings.workflow_templates` with the SmartTask
 * companions of its mapped workflows. Use wherever a solution's workflow set
 * drives RBAC/permission-group behaviour — NOT for portal template lists
 * (navigation tabs, dashboard workflow tabs, bundle selectors), which keep
 * using `mappings.workflow_templates` only.
 */
export function expandWithSmartTaskCompanions(templateKeys: readonly string[]): string[] {
  const expanded = new Set(templateKeys);
  for (const key of templateKeys) {
    for (const companion of SMART_TASK_WORKFLOW_COMPANIONS[key] ?? []) {
      expanded.add(companion);
    }
  }
  return [...expanded];
}

// ============================================================================
// CONFIGURATION REGISTRY
// ============================================================================

class IndustrySolutionConfigRegistry {
  private operationalWorkflows: Map<string, OperationalWorkflowTemplateConfig> = new Map();
  private industrySolutions: Map<string, IndustrySolutionDefinition> = new Map();
  private workflowsByIndustry: Map<string, string[]> = new Map();
  private smartTaskConfig: SmartTaskConfigRoot | null = null;
  private permissionDefaults: PermissionDefaultsConfig | null = null;
  private initialized = false;

  /**
   * Add a single operational workflow template config (called by server initializer)
   */
  addOperationalWorkflow(key: string, config: OperationalWorkflowTemplateConfig): void {
    if (config && config.template_key) {
      this.operationalWorkflows.set(key, config);
    }
  }

  /**
   * Load industry solution definitions (called by server initializer)
   */
  loadIndustrySolutions(solutions: IndustrySolutionDefinition[]): void {
    for (const solution of solutions) {
      this.industrySolutions.set(solution.solution_key, solution);

      if (solution.mappings?.workflow_templates) {
        const validTemplates = solution.mappings.workflow_templates.filter(
          (t: unknown): t is string => typeof t === 'string' && t.length > 0
        );
        // SmartTask companions (e.g. expenses-management under
        // financial-management) join the solution's workflow set for
        // RBAC/permission-group purposes only — they are not portal
        // templates, so mappings.workflow_templates stays untouched.
        const workflowKeys = expandWithSmartTaskCompanions(validTemplates).filter(
          (key) => validTemplates.includes(key) || this.operationalWorkflows.has(key),
        );
        this.workflowsByIndustry.set(solution.solution_key, workflowKeys);
      }
    }
  }

  /**
   * Set SmartTask configuration (called by server initializer)
   */
  setSmartTaskConfig(config: SmartTaskConfigRoot): void {
    this.smartTaskConfig = config;
  }

  /**
   * Set permission defaults (called by server initializer)
   */
  setPermissionDefaults(config: PermissionDefaultsConfig): void {
    this.permissionDefaults = config;
  }

  /**
   * Mark the registry as initialized (called by server initializer)
   */
  markInitialized(): void {
    this.initialized = true;
  }

  /**
   * Reset the registry for a full reload (called by admin reload)
   */
  reset(): void {
    this.operationalWorkflows.clear();
    this.industrySolutions.clear();
    this.workflowsByIndustry.clear();
    this.smartTaskConfig = null;
    this.permissionDefaults = null;
    this.initialized = false;
  }

  /**
   * Get an operational workflow template by key
   */
  getWorkflowTemplate(templateKey: string): OperationalWorkflowTemplateConfig | null {
    return this.operationalWorkflows.get(templateKey) || null;
  }

  /**
   * Get all workflow templates for a solution
   */
  getWorkflowsForSolution(solutionKey: string): OperationalWorkflowTemplateConfig[] {
    const workflowKeys = this.workflowsByIndustry.get(solutionKey) || [];
    return workflowKeys
      .map(key => this.operationalWorkflows.get(key))
      .filter((w): w is OperationalWorkflowTemplateConfig => w !== undefined);
  }

  /**
   * Get an industry solution definition by key
   */
  getSolution(solutionKey: string): IndustrySolutionDefinition | null {
    return this.industrySolutions.get(solutionKey) || null;
  }

  /**
   * Get all industry solutions
   */
  getAllSolutions(): IndustrySolutionDefinition[] {
    return Array.from(this.industrySolutions.values());
  }

  /**
   * Get the registry for external access (read-only)
   */
  getRegistry(): ConfigRegistry {
    return {
      operational_workflows: this.operationalWorkflows,
      industry_solutions: this.industrySolutions,
      workflows_by_solution: this.workflowsByIndustry,
    };
  }

  /**
   * Check if initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get SmartTask configuration
   */
  getSmartTaskConfig(): SmartTaskConfigRoot | null {
    return this.smartTaskConfig;
  }

  /**
   * Get permission defaults (feature_defaults, operations_action_permissions,
   * role_defaults). Returns null if not loaded — callers should fall back
   * to the hardcoded constants in solutionPolicyDefaults.ts.
   */
  getPermissionDefaults(): PermissionDefaultsConfig | null {
    return this.permissionDefaults;
  }

  /**
   * Get all workflow modal definitions for a specific workflow.
   *
   * The returned modals have their `config.actions` merged with the
   * workflow template's `workflow_actions`: every workflow action that
   * targets this modal's component (`renderer_kind: 'existing_modal'` +
   * `existing_modal_config.component`) is guaranteed present, so adding an
   * action to the workflow YAML can't silently drift from the modal (the
   * `face_scan` class of bug). Modal entries win field-level conflicts —
   * they carry UI-specific overrides (label, help_text, mode).
   */
  getWorkflowModals(workflowKey: string): SmartTaskModalDefinition[] {
    if (!this.smartTaskConfig || !this.smartTaskConfig.workflows[workflowKey]) {
      return [];
    }
    return this.smartTaskConfig.workflows[workflowKey].modals.map((modal) =>
      this.mergeWorkflowActionsIntoModal(workflowKey, modal),
    );
  }

  /**
   * Backfill a modal's action list from the workflow YAML's
   * workflow_actions. Only actions explicitly bound to this modal are
   * candidates — form/picker renderers are never injected. Binding is
   * explicit: `existing_modal_config.host_modal` (or, for actions that
   * launch the modal itself, `existing_modal_config.component`) must equal
   * `modal.component`. Actions bound elsewhere or unbound are never
   * injected — modal action lists are curated, this merge only prevents
   * silently-dropped bound actions (the `face_scan` class of bug).
   * Modal action fields win on key collisions.
   */
  private mergeWorkflowActionsIntoModal(
    workflowKey: string,
    modal: SmartTaskModalDefinition,
  ): SmartTaskModalDefinition {
    const workflowActions = this.operationalWorkflows.get(workflowKey)?.workflow_actions;
    if (!Array.isArray(workflowActions) || workflowActions.length === 0) {
      return modal;
    }

    const targeted = workflowActions.filter(
      (action) =>
        action.renderer_kind === 'existing_modal'
        && (action.existing_modal_config?.host_modal === modal.component
          || action.existing_modal_config?.component === modal.component),
    );
    if (targeted.length === 0) return modal;

    const targetedByKey = new Map(targeted.map((action) => [action.key, action]));

    // Fields the modal left unset are backfilled from the workflow action;
    // fields it set (label overrides, help_text, mode) always win.
    const existing = (modal.config?.actions ?? []).map((action) => {
      const wa = targetedByKey.get(action.key);
      if (!wa) return action;
      return {
        ...action,
        label: action.label ?? wa.label,
        icon: action.icon ?? wa.icon,
        color: action.color ?? wa.color,
        description: action.description ?? wa.description,
        requires_permission: action.requires_permission ?? wa.requires_permission,
        requires_vehicle: action.requires_vehicle ?? wa.requires_vehicle,
      };
    });

    const existingKeys = new Set(existing.map((action) => action.key));
    const missing = targeted
      .filter((action) => !existingKeys.has(action.key))
      .map((action) => ({
        key: action.key,
        label: action.label,
        icon: action.icon,
        color: action.color,
        description: action.description,
        requires_permission: action.requires_permission,
        requires_vehicle: action.requires_vehicle,
        mode: 'action' as const,
      }));

    return {
      ...modal,
      config: { ...modal.config, actions: [...existing, ...missing] },
    };
  }

  /**
   * Get a specific modal definition by workflow key and modal key
   */
  getModalDefinition(workflowKey: string, modalKey: string): SmartTaskModalDefinition | null {
    const modals = this.getWorkflowModals(workflowKey);
    return modals.find(m => m.key === modalKey) || null;
  }

  /**
   * Get all workflow modal definitions (across all workflows)
   */
  getAllWorkflowModals(): Array<{ workflowKey: string; workflowName: string; modal: SmartTaskModalDefinition }> {
    if (!this.smartTaskConfig) {
      return [];
    }

    const result: Array<{ workflowKey: string; workflowName: string; modal: SmartTaskModalDefinition }> = [];
    
    for (const [workflowKey, workflowConfig] of Object.entries(this.smartTaskConfig.workflows)) {
      for (const modal of this.getWorkflowModals(workflowKey)) {
        result.push({
          workflowKey,
          workflowName: workflowConfig.workflow_name,
          modal,
        });
      }
    }
    
    return result;
  }

  /**
   * Get SmartTask modal styling configuration
   */
  getModalStyling(): SmartTaskConfigRoot['modal_styling'] | null {
    return this.smartTaskConfig?.modal_styling || null;
  }

  /**
   * Get SmartTask shell constants (colors, timeouts, financial document modal keys)
   */
  getShellConstants(): SmartTaskShellConstants | null {
    return this.smartTaskConfig?.shell_constants || null;
  }

  /**
   * Get SmartTask stat cards configuration (colors, modal config, priority dots)
   */
  getStatCardsConfig(): SmartTaskStatCardsConfig | null {
    return this.smartTaskConfig?.stat_cards || null;
  }

  /**
   * Get SmartTask panel smart actions configuration (icon name mappings)
   */
  getPanelSmartActionsConfig(): SmartTaskPanelSmartActionsConfig | null {
    return this.smartTaskConfig?.panel_smart_actions || null;
  }

  /**
   * Get SmartTask display modes configuration (default tab, centric item type, unified tab mappings)
   */
  getDisplayModesConfig(): SmartTaskDisplayModesConfig | null {
    return this.smartTaskConfig?.display_modes || null;
  }

  /**
   * Get SmartTask unified filter tabs configuration
   */
  getUnifiedFilterTabs(): SmartTaskUnifiedFilterTab[] {
    return this.smartTaskConfig?.unified_filter_tabs || [];
  }
}

// ============================================================================
// SINGLETON REGISTRY INSTANCE
// ============================================================================

let registryInstance: IndustrySolutionConfigRegistry | null = null;

export function getConfigRegistry(): IndustrySolutionConfigRegistry {
  if (!registryInstance) {
    registryInstance = new IndustrySolutionConfigRegistry();
  }
  return registryInstance;
}

// ============================================================================
// DEEP MERGE UTILITIES
// ============================================================================

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === '[object Object]'
  );
}

function isKeyedObjectArray(arr: unknown[]): arr is Array<Record<string, unknown> & { key?: string; id?: string }> {
  return arr.every(
    (item) =>
      isPlainObject(item) &&
      (typeof item.key === 'string' || typeof item.id === 'string')
  );
}

function getMergeItemKey(item: Record<string, unknown> & { key?: string; id?: string }): string {
  if (typeof item.key === 'string' && item.key.length > 0) {
    return item.key;
  }
  return String(item.id);
}

function mergeArrays(base: unknown[], override: unknown[]): unknown[] {
  if (!isKeyedObjectArray(base) || !isKeyedObjectArray(override)) {
    // Non-keyed arrays are replaced entirely by the override.
    return override;
  }

  const merged = [...base];
  for (const sourceItem of override) {
    const sourceKey = getMergeItemKey(sourceItem);
    const index = merged.findIndex(
      (baseItem) => getMergeItemKey(baseItem) === sourceKey
    );
    if (index >= 0) {
      merged[index] = deepMergeConfig(merged[index], sourceItem);
    } else {
      merged.push(sourceItem);
    }
  }
  return merged;
}

export function deepMergeConfig<T>(base: T, override: unknown): T {
  if (override === undefined || override === null) {
    return base;
  }

  if (Array.isArray(base) && Array.isArray(override)) {
    return mergeArrays(base, override) as T;
  }

  if (isPlainObject(base) && isPlainObject(override)) {
    const result: Record<string, unknown> = { ...base };
    for (const key of Object.keys(override)) {
      const sourceValue = override[key];
      if (sourceValue === undefined) continue;
      result[key] = deepMergeConfig(result[key], sourceValue);
    }
    return result as T;
  }

  // For incompatible or primitive overrides, the override wins.
  return override as T;
}

// ============================================================================
// RESOLVER: Merges local config with database overrides
// ============================================================================

export async function resolveIndustrySolution(
  solutionKey: string,
  clientOverrides?: Record<string, unknown>
): Promise<ResolvedIndustrySolution | null> {
  const registry = getConfigRegistry();
  
  if (!registry.isInitialized()) {
    throw new Error('ConfigRegistry not initialized. Call initializeConfigRegistry() first.');
  }

  const solutionDef = registry.getSolution(solutionKey);
  if (!solutionDef) {
    return null;
  }

  const workflows = registry.getWorkflowsForSolution(solutionKey);

  // Aggregate configs from all mapped workflow templates using deep merge so
  // that a later workflow extending the same rule does not wipe nested config.
  let mergedRules: Record<string, unknown> = {};
  let mergedFeatures: Record<string, unknown> = {};
  let mergedTerminology: Record<string, string> = {};
  let mergedQuickActions: QuickActionConfig[] = [];

  for (const workflow of workflows) {
    const st = workflow.config?.smart_task;
    if (st?.rules) {
      mergedRules = deepMergeConfig(mergedRules, st.rules) as Record<string, unknown>;
    }
    if (st?.features) {
      mergedFeatures = deepMergeConfig(mergedFeatures, st.features) as Record<string, unknown>;
    }
    if (st?.terminology) {
      mergedTerminology = deepMergeConfig(mergedTerminology, st.terminology) as Record<string, string>;
    }
    if (st?.quickActions) {
      mergedQuickActions = deepMergeConfig(mergedQuickActions, st.quickActions) as QuickActionConfig[];
    }
  }

  // Apply solution-level overrides on top of workflow defaults
  if (solutionDef.mappings?.custom_terminology) {
    mergedTerminology = deepMergeConfig(
      mergedTerminology,
      solutionDef.mappings.custom_terminology
    ) as Record<string, string>;
  }
  const solutionQuickActions = solutionDef.mappings?.custom_quick_actions || [];
  mergedQuickActions = deepMergeConfig(mergedQuickActions, solutionQuickActions) as QuickActionConfig[];

  return {
    solution_key: solutionDef.solution_key,
    name: solutionDef.name,
    description: solutionDef.description,
    workflows,
    merged_config: {
      rules: mergedRules,
      features: mergedFeatures,
      terminology: mergedTerminology,
      quickActions: mergedQuickActions,
    },
    dashboards: {},
    subPages: {},
    custom_overrides: clientOverrides,
  };
}

/**
 * Get resolved workflow template with all configurations
 */
export async function resolveWorkflowTemplate(templateKey: string): Promise<OperationalWorkflowTemplateConfig | null> {
  const registry = getConfigRegistry();
  
  if (!registry.isInitialized()) {
    throw new Error('ConfigRegistry not initialized. Call initializeConfigRegistry() first.');
  }

  return registry.getWorkflowTemplate(templateKey);
}

// Export for testing/debugging
export { IndustrySolutionConfigRegistry };
